/**
 * A failed turn's `X-Trace-Id`, with a way to copy it.
 *
 * The one line on an error card written for support rather than for the user:
 * it is the key that finds the turn in the agent's and the plugin's logs
 * (`docs/logging-and-observability.md` §3). Monospaced because it is read
 * character by character, and copyable because thirty-two hex characters
 * retyped from a screenshot arrive wrong more often than not.
 *
 * Shared by the two places an error can appear — above the composer for a
 * refusal that never became a turn, and in the flow for one that did — because
 * the copy-confirmation state is fiddly enough that two copies of it would
 * drift. The confirmation appears only when the text actually landed: this
 * shell denies clipboard permission, so `navigator.clipboard.writeText` alone
 * silently copies nothing, and `copyText` reports whether its fallback worked.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { copyText } from "../../lib/clipboard";

/** How long the "copied" confirmation stands in for a toast. */
const COPIED_FEEDBACK_MS = 1600;

export function TraceReference({ traceId, className }: { traceId: string; className: string }) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <p className={className}>
      <span>{t("chat.error.reference")}: </span>
      <code>{traceId}</code>
      <button
        type="button"
        className="composer-error-copy"
        aria-label={t("chat.error.copyReference")}
        title={t("chat.error.copyReference")}
        onClick={() => {
          void copyText(traceId).then((ok) => {
            if (ok) setCopied(true);
          });
        }}
      >
        {copied ? t("chat.copied") : t("chat.error.copyReference")}
      </button>
    </p>
  );
}
